import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Ng2IzitoastService } from 'ng2-izitoast';
import { ClientesService } from 'src/app/services/clientes.service';
import { GruposService } from 'src/app/services/grupos.service';
import { LoginService } from 'src/app/services/login.service';
import { PaquetesService } from 'src/app/services/paquetes.service';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-v2inicio',
  templateUrl: './v2inicio.component.html',
  styleUrls: ['./v2inicio.component.scss']
})
export class V2inicioComponent implements OnInit {

  /** Flag that controls which step of the registration modal is visible */
  public submitData = false;
  /** Video url that we receive from the backend and sanitize for the iframe/video tag */
  public videoRegistroUrl: SafeResourceUrl | null = null;
  /** Loading state for contenido endpoint */
  public loadingContenido = false;
  /** Loading state for the registration request */
  public submittingForm = false;
  /** Reactive form instance that holds all user inputs */
  public registerForm: FormGroup;
  /** Login form to authenticate existing users from the landing page */
  public loginForm: FormGroup;
  /** Tracks the logged in user data to show their name / info modal */
  public usuarioActual: any = null;
  /** Loading flag for the login action */
  public loginSubmitting = false;
  /** Available grupos so the student can bind the registration to one */
  public grupos: Array<{ id: number; nombre: string; carrera?: string; escuela?: string }> = [];
  /** Group loaded from cliente/inicio/:grupo or ?grupo= so the student does not have to select it */
  public grupoPreseleccionado: string | null = null;
  /** Full detail for the preloaded group, including school/career */
  public grupoPrecargado: any = null;
  /** Loading state for preloaded group details */
  public loadingGrupo = false;
  /** Cached contenido response so we can reuse other fields later if needed */
  public contenido: any;
  /** Base url used to build absolute asset paths */
  public readonly environmentBase = environment.BASE_URL;

  get nombrePerfil(): string {
    if (!this.usuarioActual) {
      return '';
    }

    const nombreCompleto = [
      this.usuarioActual.first_name || this.usuarioActual.nombre,
      this.usuarioActual.last_name,
      this.usuarioActual.apellido_materno
    ].filter(Boolean).join(' ');

    return nombreCompleto || 'Usuario';
  }

  constructor(
    private modalService: NgbModal,
    private paquetesService: PaquetesService,
    private clientesService: ClientesService,
    private gruposService: GruposService,
    private loginService: LoginService,
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private iziToast: Ng2IzitoastService,
    private sanitizer: DomSanitizer
  ) {
    /** The form is built in the constructor so template bindings are always defined */
    this.registerForm = this.fb.group({
      grupo: ['', Validators.required],
      nombre: ['', Validators.required],
      apellidoPaterno: ['', Validators.required],
      apellidoMaterno: ['', Validators.required],
      celular: ['', [Validators.required, Validators.minLength(10)]],
      correo: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(3)]],
      passwordConfirm: ['', Validators.required],
      acceptTerms: [false]
    });

    this.loginForm = this.fb.group({
      username: ['', Validators.required],
      password: ['', Validators.required]
    });
  }

  ngOnInit(): void {
    /** On init we load contenido (video / copy) and the grupos catalog */
    this.resolveGrupoPreseleccionado();
    this.loadContenido();
    if (!this.grupoPreseleccionado) {
      this.loadGrupos();
    }
    this.loadLoggedUser();
  }

  private resolveGrupoPreseleccionado(): void {
    const grupoId =
      this.route.snapshot.paramMap.get('grupo') ||
      this.route.snapshot.queryParamMap.get('grupo');

    if (!grupoId) {
      return;
    }

    this.grupoPreseleccionado = grupoId;
    this.registerForm.patchValue({ grupo: grupoId });
    this.loadDetalleGrupo(grupoId);
  }

  /**
   * Requests the contenido (videos, copy) used across the funnel.
   * The endpoint already exists in the Django project (GetContenido view).
   */
  private loadContenido(): void {
    this.loadingContenido = true;
    this.paquetesService.getContenido().subscribe({
      next: (response:any) => {
        this.loadingContenido = false;
        //if (response?.success && response.content?.length) {
          this.contenido = response.content[0];
          const videoPath = this.contenido.video_registro;
          this.videoRegistroUrl = `${this.environmentBase}${videoPath}`
          if (videoPath) {
            /** The backend returns a relative path so we build the absolute url and sanitize it */
            this.videoRegistroUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
              `${this.environmentBase}${videoPath}`
            );
          } else {
            /** We keep a comment when data is missing so QA knows there is no video configured */
            console.warn('Contenido endpoint respondió sin video_registro, no se mostrará el iframe.');
          }
        //}
      },
      error: (error) => {
        this.loadingContenido = false;
        console.error('No se pudo obtener el contenido inicial', error);
      }
    });
  }

  /**
   * Fetches the grupos catalog so the client can associate their registration.
   * This relies on the public /api/getGruposNombre/ endpoint handled by GruposService.
   */
  private loadGrupos(): void {
    this.gruposService.getGruposNombre().subscribe({
      next: (response:any) => {
        if (response?.success && response.content) {
          this.grupos = response.content.map((grupo: any) => ({
            id: grupo.id,
            nombre: grupo.nombre,
            carrera: grupo.carrera,
            escuela: grupo.escuela
          }));
        }
      },
      error: (error) => console.error('No se pudieron cargar los grupos disponibles', error)
    });
  }

  private loadDetalleGrupo(grupoId: string): void {
    this.loadingGrupo = true;
    this.gruposService.getDetalleGrupo({ grupo: grupoId }).subscribe({
      next: (response:any) => {
        this.loadingGrupo = false;
        if (response?.success && response.content) {
          this.grupoPrecargado = response.content;
          this.registerForm.patchValue({ grupo: response.content.id || grupoId });
        }
      },
      error: (error) => {
        this.loadingGrupo = false;
        console.error('No se pudo cargar el grupo preseleccionado', error);
      }
    });
  }

  /**
   * Opens the appropriate modal depending on the session state.
   */
  onLoginButtonClick(loginModal: any, profileModal: any): void {
    if (this.usuarioActual) {
      this.modalService.open(profileModal, { size: 'md' });
    } else {
      this.modalService.open(loginModal, { size: 'md' });
    }
  }

  openModal(modal: any): void {
    if (this.usuarioActual) {
      return;
    }

    this.modalService.open(modal, { size: 'lg' });
  }

  dismissAll(): void {
    this.modalService.dismissAll();
  }

  /**
   * Handles the wizard flow:
   *  - First click validates the base form data and reveals the terms/video.
   *  - Second click (already in submitData step) calls the backend to create the account.
   */
  onSubmit(): void {
    if (this.usuarioActual) {
      this.iziToast.show({
        title: 'Ya estas registrado.',
        titleColor: '#ffffff',
        color: '#CA0B00',
        position: 'topRight',
        closeOnEscape: true
      });
      return;
    }

    if (!this.submitData) {
      if (this.validateForm()) {
        this.submitData = true;
      }
      return;
    }

    if (!this.registerForm.get('acceptTerms')?.value) {
      this.iziToast.show({
        title: 'Debes aceptar los términos antes de continuar',
        titleColor: '#ffffff',
        color: '#CA0B00',
        position: 'topRight',
        closeOnEscape: true
      });
      return;
    }

    this.registerCliente();
  }

  /**
   * Helper that marks controls as touched and ensures the passwords match.
   */
  private validateForm(): boolean {
    this.registerForm.markAllAsTouched();
    if (this.registerForm.invalid) {
      this.iziToast.show({
        title: 'Por favor completa los campos obligatorios',
        titleColor: '#ffffff',
        color: '#CA0B00',
        position: 'topRight',
        closeOnEscape: true
      });
      return false;
    }

    const password = this.registerForm.get('password')?.value;
    const confirm = this.registerForm.get('passwordConfirm')?.value;

    if (password !== confirm) {
      this.iziToast.show({
        title: 'Las contraseñas no coinciden',
        titleColor: '#ffffff',
        color: '#CA0B00',
        position: 'topRight',
        closeOnEscape: true
      });
      return false;
    }

    return true;
  }

  /**
   * Builds the payload required by /api/agregarClienteGrupoV2/ and,
   * if the backend accepts it, logs the user in so they can keep the funnel.
   */
  private registerCliente(): void {
    this.submittingForm = true;
    const formValue = this.registerForm.value;
    const payload = {
      grupo: formValue.grupo,
      first_name: formValue.nombre,
      last_name: formValue.apellidoPaterno,
      apellido_materno: formValue.apellidoMaterno,
      celular: formValue.celular,
      email: formValue.correo,
      password: formValue.password,
      username: formValue.celular
    };

    this.clientesService.agregarClienteGrupoV2(payload).subscribe({
      next: (response:any) => {
        this.submittingForm = false;
        if (response?.success) {
          this.iziToast.show({
            title: '¡Bienvenido! Tu cuenta fue creada correctamente.',
            titleColor: '#f8f8ff',
            color: '#4CAF50',
            position: 'topRight',
            closeOnEscape: true
          });
          this.dismissAll();
          this.iniciarSesion(formValue.celular, formValue.password);
        } else {
          const errorMessage =
            response?.error || 'No pudimos completar el registro, intenta más tarde.';
          this.iziToast.show({
            title: errorMessage,
            titleColor: '#ffffff',
            color: '#CA0B00',
            position: 'topRight',
            closeOnEscape: true
          });
        }
      },
      error: (error) => {
        this.submittingForm = false;
        console.error('Error al registrar el cliente', error);
        this.iziToast.show({
          title: 'Ocurrió un error al registrar tu cuenta',
          titleColor: '#ffffff',
          color: '#CA0B00',
          position: 'topRight',
          closeOnEscape: true
        });
      }
    });
  }

  /**
   * After a successful signup we trigger /api/auth/ to obtain the token
   * and redirect the student straight to la selección de paquetes.
   */
  private iniciarSesion(username: string, password: string): void {
    this.loginService.loginUser({ username, password }).subscribe({
      next: (response) => {
        if (response?.token) {
          this.loginService.guardarToken(response.token);
          const redirect = this.route.snapshot.queryParamMap.get('redirect');
          if (redirect) {
            this.router.navigateByUrl(redirect).then(() => window.location.reload());
            return;
          }
          this.router.navigate(['cliente/paquetes']).then(() => window.location.reload());
        }
      },
      error: (error) => console.error('No se pudo iniciar sesión automáticamente', error)
    });
  }


  /**
   * Loads the current user data if a token is present.
   */
  private loadLoggedUser(): void {
    const token = localStorage.getItem('token');
    if (!token) {
      this.usuarioActual = null;
      return;
    }

    this.loginService.getDataCliente().subscribe({
      next: (response: any) => {
        if (response?.success && response.content) {
          this.usuarioActual = response.content;          
          if (!response.content.first_name && !response.content.nombre) {
            this.usuarioActual.first_name="Usuario"
          }          
        } else {
          this.usuarioActual = null;
        }
      },
      error: () => {
        this.loginService.cerrarSesion();
        this.usuarioActual = null;
      }
    });
  }

  /**
   * Handles manual login flow from the landing modal.
   */
  onLoginSubmit(): void {
    this.loginForm.markAllAsTouched();
    if (this.loginForm.invalid) {
      return;
    }

    this.loginSubmitting = true;
    this.loginService.loginUser(this.loginForm.value).subscribe({
      next: (response) => {
        this.loginSubmitting = false;
        if (response?.token) {
          this.loginService.guardarToken(response.token);
          this.iziToast.show({
            title: 'Sesion iniciada correctamente',
            titleColor: '#f8f8ff',
            color: '#4CAF50',
            position: 'topRight',
            closeOnEscape: true
          });
          this.loginForm.reset();
          this.dismissAll();
          const redirect = this.route.snapshot.queryParamMap.get('redirect');
          if (redirect) {
            this.router.navigateByUrl(redirect);
            return;
          }
          this.loadLoggedUser();
        } else {
          this.iziToast.show({
            title: 'No pudimos iniciar sesion',
            titleColor: '#ffffff',
            color: '#CA0B00',
            position: 'topRight',
            closeOnEscape: true
          });
        }
      },
      error: () => {
        this.loginSubmitting = false;
        this.iziToast.show({
          title: 'Credenciales incorrectas',
          titleColor: '#ffffff',
          color: '#CA0B00',
          position: 'topRight',
          closeOnEscape: true
        });
      }
    });
  }

  /**
   * Logs the current user out and closes any open modal.
   */
  logout(): void {
    this.loginService.cerrarSesion();
    this.usuarioActual = null;
    this.loginForm.reset();
    this.dismissAll();
  }


}
